import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import { generateBusinessInsights, getInsightIcon, getPriorityColors } from '../utils/businessInsights'

const INITIAL_INSIGHT_LIMIT = 3

const InsightsPanel = ({ currentMetrics, historicalData, industryBenchmarks = null, hasAutomationConfigured = false }) => {
  const { t } = useTranslation('dashboard')
  const [activeTab, setActiveTab] = useState('insights')
  const [expandedInsight, setExpandedInsight] = useState(null)
  const [showAllInsights, setShowAllInsights] = useState(false)
  const [hideCounts, setHideCounts] = useState(false)
  const contactEmail = 'info@sweetestadsmedia.com'
  const contactSubject = 'Interactive Business inquiry'
  const contactBody = [
    t('insights.contact.bodyGreeting'),
    '',
    t('insights.contact.bodyInquiry'),
    '',
    t('insights.contact.bodyContactInfo'),
  ].join('\n')
  const contactHref = `mailto:${contactEmail}?subject=${encodeURIComponent(contactSubject)}&body=${encodeURIComponent(contactBody)}`

  if (!currentMetrics) {
    return (
      <section className="flex h-full flex-col rounded-[14px] border border-gray-200 bg-white p-5 sm:p-6">
        <h2 className="text-base font-bold text-gray-900">{t('insights.businessIntelligence')}</h2>
        <p className="mt-1 text-xs text-gray-500">{t('insights.subtitle')}</p>
        <p className="mt-6 text-sm text-gray-500">{t('insights.emptyState')}</p>
      </section>
    )
  }

  const businessData = generateBusinessInsights(currentMetrics, historicalData, industryBenchmarks)
  const { insights, recommendations, alerts, opportunities } = businessData
  const opportunitiesBadgeCount = Math.max(opportunities.length, 1)
  const tabs = [
    { id: 'insights', label: t('insights.tabs.insights'), count: insights.length },
    { id: 'recommendations', label: t('insights.tabs.actions'), count: recommendations.length },
    { id: 'alerts', label: t('insights.tabs.alerts'), count: alerts.length },
    { id: 'opportunities', label: t('insights.tabs.opportunities'), count: opportunitiesBadgeCount },
  ]

  const handleClearAll = () => setHideCounts(true)

  const activeData = activeTab === 'opportunities' && !hasAutomationConfigured
    ? []
    : {
      insights,
      recommendations,
      alerts,
      opportunities,
    }[activeTab] || insights

  const visibleData = activeTab === 'insights' && !showAllInsights
    ? activeData.slice(0, INITIAL_INSIGHT_LIMIT)
    : activeData

  const getEmptyMessage = () => {
    const messages = {
      insights: t('insights.messages.noSpecificInsights'),
      recommendations: t('insights.messages.noImmediateActions'),
      alerts: t('insights.messages.noCriticalAlerts'),
      opportunities: hasAutomationConfigured
        ? t('insights.messages.noCurrentOpportunities')
        : t('insights.messages.noAutomationOpportunities'),
    }
    return messages[activeTab] || t('charts.noDataMessage')
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    setShowAllInsights(false)
    setExpandedInsight(null)
  }

  return (
    <section className="flex h-full flex-col overflow-hidden rounded-[14px] border border-gray-200 bg-white">
      <header className="px-5 pt-5 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">{t('insights.businessIntelligence')}</h2>
            <p className="mt-1 text-xs text-gray-500">{t('insights.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleClearAll}
            className="shrink-0 text-xs font-semibold text-[#E1174C] transition-colors hover:text-[#C91545] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
          >
            {t('insights.clearAlerts')} <span aria-hidden="true">×</span>
          </button>
        </div>

        <div role="tablist" aria-label={t('insights.businessIntelligence')} className="mt-4 flex gap-1.5 overflow-x-auto border-b border-gray-200 pb-3">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                id={`business-insights-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="business-insights-panel"
                onClick={() => handleTabChange(tab.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-[7px] px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-1 ${
                  isActive
                    ? 'bg-[#FFE5EC] text-[#E1174C]'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                {tab.label}
                {!hideCounts && tab.count > 0 && (
                  <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-3 ${
                    isActive ? 'bg-[#FF1B54] text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </header>

      <div id="business-insights-panel" role="tabpanel" aria-labelledby={`business-insights-tab-${activeTab}`} className="flex flex-1 flex-col px-5 py-4 sm:px-6">
        {activeData.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
            <div className="mb-3 text-gray-400">
              {activeTab === 'opportunities' ? <Bot className="h-9 w-9 stroke-[1.75]" aria-hidden="true" /> : null}
            </div>
            <p className="text-sm text-gray-500">{getEmptyMessage()}</p>
            {activeTab === 'opportunities' && (
              <>
                <p className="mt-3 text-sm text-gray-500">{t('insights.contact.learnMore')}</p>
                <a
                  href={contactHref}
                  className="mt-5 inline-flex rounded-lg bg-[#FF1B54] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#E1174C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
                >
                  {t('insights.contact.cta')}
                </a>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {visibleData.map((item, index) => (
              <InsightCard
                key={`${activeTab}-${index}`}
                item={item}
                index={index}
                isExpanded={expandedInsight === `${activeTab}-${index}`}
                onToggleExpand={() => setExpandedInsight(
                  expandedInsight === `${activeTab}-${index}` ? null : `${activeTab}-${index}`
                )}
                type={activeTab}
                t={t}
              />
            ))}
          </div>
        )}

        {activeData.length > 0 && (
          <footer className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3 text-xs text-gray-500">
            <span>
              {activeTab === 'insights'
                ? t('insights.showingInsightsCount', { shown: visibleData.length, total: activeData.length })
                : t('insights.showingCount', {
                  count: activeData.length,
                  type: tabs.find((tab) => tab.id === activeTab)?.label?.toLowerCase() || activeTab,
                })}
            </span>
            {activeTab === 'insights' && activeData.length > INITIAL_INSIGHT_LIMIT && (
              <button
                type="button"
                aria-expanded={showAllInsights}
                onClick={() => setShowAllInsights((showAll) => !showAll)}
                className="font-semibold text-[#E1174C] transition-colors hover:text-[#C91545] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
              >
                {showAllInsights ? t('insights.showFewerInsights') : t('insights.seeAllInsights')}
                <span aria-hidden="true">{showAllInsights ? ' ↑' : ' →'}</span>
              </button>
            )}
          </footer>
        )}
      </div>
    </section>
  )
}

const InsightCard = ({ item, index, isExpanded, onToggleExpand, type, t }) => {
  const icon = getInsightIcon(item.iconType || item.type)
  const isInsightTab = type === 'insights'
  const needsReview = ['warning', 'critical'].includes(item.type) || item.priority === 'critical'
  const statusLabel = needsReview ? t('insights.signalReview') : t('insights.signalPositive')
  const statusClass = needsReview
    ? 'bg-[#FFE5EC] text-[#C91545]'
    : 'bg-emerald-50 text-emerald-700'
  const iconClass = needsReview
    ? 'bg-[#FFE5EC] text-[#E1174C]'
    : 'bg-emerald-50 text-emerald-700'
  const priorityColors = getPriorityColors(item.priority)

  return (
    <article className={`flex items-start gap-3 rounded-[10px] border px-3.5 py-3 ${
      needsReview && isInsightTab ? 'border-[#FFD4DF] bg-[#FFF8FA]' : 'border-gray-200 bg-white'
    }`}>
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${iconClass}`} aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-[13px] font-semibold leading-4 text-gray-900">{item.title}</h3>
        <p className="mt-0.5 text-xs leading-4 text-gray-500">{item.message}</p>
        {(item.category || item.metric || item.actionRequired) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-gray-500">
            {item.category && <span className="capitalize">{t(`insights.categories.${item.category}`, item.category)}</span>}
            {item.actionRequired && <span className="font-medium text-[#C91545]">{t('insights.actionRequired')}</span>}
            {item.metric && (
              <span className="text-emerald-700">
                {item.metric.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())}
              </span>
            )}
          </div>
        )}
        {item.actions && (
          <div className="mt-2">
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={`insight-actions-${type}-${index}`}
              onClick={onToggleExpand}
              className="text-xs font-medium text-[#E1174C] hover:text-[#C91545] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54]"
            >
              {isExpanded ? t('insights.hideActionSteps') : t('insights.showActionSteps')}
            </button>
            {isExpanded && (
              <ul id={`insight-actions-${type}-${index}`} className="mt-2 space-y-1 rounded-md bg-gray-50 p-2 text-xs text-gray-600">
                {item.actions.map((action, actionIndex) => <li key={actionIndex}>{action}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
      {isInsightTab ? (
        <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${statusClass}`}>
          {statusLabel}
        </span>
      ) : item.priority ? (
        <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium ${priorityColors}`}>
          {t(`insights.priorities.${item.priority}`, item.priority)}
        </span>
      ) : null}
    </article>
  )
}

export default InsightsPanel
